/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  combineReducers,
  configureStore,
  createListenerMiddleware
} from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";

import { appVisibilityMiddleware } from "./appVisibility";
import { registerEasRouteRestoreListeners } from "./eam";
import { registerErrorDisplayListeners } from "./errorDisplay";
import { NetworkResource } from "../model/enums";
import { resourceAvailabilityChanged } from "./networkAvailability/networkAvailabilitySlice";
import {
  postSigninStartupSequence,
  refreshInHomeLocation,
  registerStartupRecoveryListeners,
  selectHasStartedUp
} from "./postSigninStartup";
import RootReducer from "./root.reducers";
import { activateProfile, refreshProfileScopedData } from "./profiles";

/**
 * Unity Web Redux store — single source of truth for all UI state.
 *
 * Middleware configuration disables serializable and immutable checks
 * for performance on resource-constrained SmartTV hardware.
 */
// Enable Redux DevTools only for non-production builds. Webpack sets
// `mode: "production"` for release builds which results in
// `process.env.NODE_ENV === 'production'` when built with the default
// DefinePlugin behavior. Use that to toggle DevTools at runtime.
const enableReduxDevTools = process.env.NODE_ENV !== "production";

// ─── Listener middleware ──────────────────────────────────────────────────────
// Wires cross-feature reactions at the store (orchestrator) level so that
// individual feature slices and bridges remain decoupled.
// See REDUX_GOVERNANCE.md §6.3 — Cross-Slice Communication.
const listenerMiddleware = createListenerMiddleware();

const UnityAppStore = configureStore({
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      thunk: true,
      actionCreatorCheck: false,
      immutableCheck: false,
      serializableCheck: false
    })
      .prepend(listenerMiddleware.middleware)
      .concat(appVisibilityMiddleware),
  reducer: combineReducers(RootReducer),
  devTools: enableReduxDevTools
});

/** Inferred root state type from the store's combined reducer. */
export type UnityRootState = ReturnType<typeof UnityAppStore.getState>;

/** Typed dispatch that includes thunk dispatch signatures. */
export type UnityAppDispatch = typeof UnityAppStore.dispatch;

// React to network graph settlements: when the INTERNET resource becomes
// available post-signin, re-check the in-home location status.
//
// Design note: we listen to resourceAvailabilityChanged (rather than a
// dedicated networkInternetAvailable signal action) because hasStartedUp
// already handles seed-time suppression, making a separate signal
// redundant. This keeps the bridge stateless and purely translational.
// The trade-off is that the listener must inspect the payload to filter
// for INTERNET — a named signal action would be more self-documenting
// but would require the bridge to carry extra state. Either approach is
// valid per REDUX_GOVERNANCE.md §6.3; this was chosen per reviewer
// preference after hasStartedUp was introduced.
//
// Cross-feature wiring lives at the store (orchestrator) level per REDUX_GOVERNANCE.md §6.3.
listenerMiddleware.startListening({
  actionCreator: resourceAvailabilityChanged,
  effect: (action, listenerApi) => {
    if (
      action.payload.resource !== NetworkResource.INTERNET ||
      !action.payload.isAvailable
    )
      return;
    // RTK's untyped createListenerMiddleware() requires an explicit cast to
    // access dispatch/getState with the store's concrete types.
    const { dispatch, getState } = listenerApi as unknown as {
      dispatch: UnityAppDispatch;
      getState: () => UnityRootState;
    };
    if (!selectHasStartedUp(getState())) return;
    dispatch(refreshInHomeLocation());
  }
});

// Cross-surface error arbitration between the OSD and the full-screen dialog
// (rules R4/R5 — see errorDisplay.listeners.ts). Registered here rather than in
// either slice so neither gains a dependency on the other, per
// REDUX_GOVERNANCE.md §6.3.
registerErrorDisplayListeners(listenerMiddleware);

// Returns the viewer to the screen an EAS alert interrupted. At the store
// because an alert can take the screen from anywhere, and because the route
// has to be recorded in the same tick the alert appears — see
// easRouteRestore.ts for why this is not a mounted component.
registerEasRouteRestoreListeners(listenerMiddleware);

// Carries out the Try Again button the post-sign-in startup overlay offers
// once device features have failed repeatedly. At the store because the
// protected area mounts on authentication, so no single screen is guaranteed
// to be showing when the user presses it.
registerStartupRecoveryListeners(listenerMiddleware);

// A profile became active — either the first selection or a later switch.
// Both invalidate everything in `PROFILE_SCOPED_FETCHES`; which path re-runs
// that list is the only difference between them.
listenerMiddleware.startListening({
  actionCreator: activateProfile.fulfilled,
  effect: (action, listenerApi) => {
    const { dispatch, getState } = listenerApi as unknown as {
      dispatch: UnityAppDispatch;
      getState: () => UnityRootState;
    };

    // Re-picking the profile already in use activates nothing, so nothing it
    // owns went stale. Without this, choosing your own profile on the picker
    // re-requests the whole profile-scoped set for no change at all.
    const result = (action as { payload?: { didActivate?: boolean } }).payload;
    if (result !== undefined && result.didActivate === false) {
      return;
    }

    const startupState = getState().postSigninStartup;

    // The sequence activated this profile itself (sole profile auto-selected,
    // or a persisted preference reused) and goes on to run the list at its
    // Steps 5-6. Refreshing here too would double every one of those calls.
    if (startupState.isStarting) {
      return;
    }

    // First selection: the sequence parked at its profile gate and has yet to
    // reach Steps 5-6. Resuming it covers the list along with everything else
    // still owed — channels, in-home location, DRM, the WebSocket.
    if (!startupState.hasStartedUp) {
      dispatch(postSigninStartupSequence());
      return;
    }

    // A switch on a started-up session. Nothing else about the session
    // changed, so only the profile-scoped set is re-requested.
    dispatch(refreshProfileScopedData());
  }
});

/** Pre-typed `useDispatch` hook — use instead of plain `useDispatch`. */
export const useUnityDispatch = useDispatch.withTypes<UnityAppDispatch>();

/** Pre-typed `useSelector` hook — use instead of plain `useSelector`. */
export const useUnitySelector = useSelector.withTypes<UnityRootState>();

export default UnityAppStore;
